import {
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  Image,
  Link,
  Text,
  VStack,
} from '@chakra-ui/react';
import NextLink from 'next/link';
import isMobileDevice from 'ismobilejs';
import type { GetServerSideProps, NextPage } from 'next';
import NextImage from 'next/image';
import { SPECIALS } from '../../../common/constants';
import { ArticleStatus, shouldNotShow } from '../../../common/libs/article';
import { DiscloseScope } from '../../../common/libs/discloseScope';
import { sendAnalyticsEvent } from '../../../common/libs/googleAnalytics';
import { hasDemoAuthority } from '../../../common/libs/userRoleType';
import ArticlesGrid, {
  ArticleHeader,
  specialPageRegularColumns,
} from '../../../components/ArticlesGrid';
import * as date from '../../../server/libs/date';
import { getGaCid } from '../../../server/libs/googleAnalytics';
import { fetchAccountIntegrationProfiles } from '../../../server/libs/id';
import prisma from '../../../server/libs/prisma';
import withAuth from '../../../server/libs/withAuth';
import {
  getArticleIdsWithTagIds,
  getArticleIdsWithAndTagIds,
} from '../../../server/libs/tag';
import { tagsString } from '../../../common/libs/tag';
import createUserAccessHistory from '../../../server/libs/createUserAccessHistory';
import { Tag } from '../../tagsearch/[tagIds]';
import { AntaaAccountType } from '../../../common/libs/antaaAccountType';
import useSpecialPage from '../../../hooks/useSpecialPage';
import {
  isPublisherAccount,
  isDoctorRecruiterAccount,
  isLicenseVerified,
} from '../../../common/libs/user';
import { getSpecialArticleProps } from '../../../server/libs/specialArticles';
import { getTagsWithTagSettings } from '../../../common/libs/specials';

export const setting = {
  specialPageSlug: 'ctd-ild',
  specialPageShortName: SPECIALS.ctdIld.name,
  tagTexts: SPECIALS.ctdIld.tags,
  andTagTexts: SPECIALS.ctdIld.andTags,
  specialPagePath: SPECIALS.ctdIld.link,
  specialPageTitle: SPECIALS.ctdIld.alt,
  sideBanner: () => null,
  listTitle: '#膠原病に伴う間質性肺疾患　に関するスライド', // 一覧タイトルをタグ配列から生成せず固定文字列にする場合はここに設定
};

const mainImagePath = SPECIALS.ctdIld.mainImage ?? '';
const userAccessHistoryOperation = `special_${setting.specialPageShortName}`; // user_access_histories.operationカラムの値のため合計16文字以内
const uploadTag = setting.andTagTexts?.[0][0];
const specialPageSlug = setting.specialPageSlug;

const description = (
  <>
    <Text fontSize="sm" fontWeight="bold" mb={2}>
      企画概要
    </Text>
    <Text fontSize="sm">
      膠原病に伴う間質性肺疾患（CTD-ILD）は、膠原病と呼吸器の両側面からの診療が求められる、複雑かつ個別性の高い疾患であり、診断・治療・予後の見通しに悩むケースも少なくありません。
      <br />
      とくに、画像評価、呼吸器症状の変化、免疫抑制治療の適応判断など、日々の診療には“迷い”がつきものです。
      <br />
      Antaaでは、こうした<strong>実臨床の工夫や悩みに役立つスライド投稿</strong>
      を募集しています。
      <br />
      <br />
      たとえば：
      <br />
      ・診断のきっかけとなった画像や症状の変化
      <br />
      ・抗線維化薬や免疫抑制薬の導入・切り替えの実例
      <br />
      ・呼吸器内科や膠原病内科の先生方から見た連携の工夫
      <br />
      ・急性増悪や難治症例の対応で意識しているポイント
      <br />
      <br />
      現場で得た知見や実感が、他の先生の診療を支えるヒントになります。
      <br />
      ぜひ、あなたの経験を一枚のスライドに込めてご投稿ください。
    </Text>
    <Flex justifyContent="flex-end" mt={4}>
      <NextLink href={`/article/upload?tags=${uploadTag}`} passHref>
        <Button
          size="sm"
          as="a"
          color="white"
          bg="brand.500"
          onClick={() => {
            sendAnalyticsEvent(
              'special_contents',
              'click',
              `${setting.specialPageShortName}Upload`,
            );
          }}
          textAlign="center"
          display="inline-block"
          p={2}
          pl={{ base: 2, md: 4 }}
          pr={{ base: 2, md: 4 }}
          ml={4}
          height="auto"
          w={{ base: '50%', md: '30%' }}
          _hover={{
            opacity: '0.8',
          }}
        >
          <Text as="span">#{uploadTag}</Text>
          <br />
          <Text as="span" fontSize="xs">
            でスライド投稿
          </Text>
        </Button>
      </NextLink>
    </Flex>
  </>
);

// ここより下は特集ページごとの固有の内容は無し

type PageContext = {
  tags: Tag[];
  articles: ArticleHeader[];
  isMobile: boolean;
  isDemoAccount: boolean;
  isAuthenticated: boolean;
  isLicenseVerified: boolean;
  isCorporationAccount: boolean;
  isPublisherAccount: boolean;
  isDoctorRecruiterAccount: boolean;
  isPrivate: boolean;
};

const ICtdIldPage: NextPage<PageContext> = ({
  tags,
  articles,
  isMobile,
  isDemoAccount,
  isAuthenticated,
  isLicenseVerified,
  isCorporationAccount,
  isPublisherAccount,
  isDoctorRecruiterAccount,
  isPrivate,
}) => {
  const {
    specialPageTitle,
    specialPagePath,
    specialPageShortName,
    sideBanner,
    listTitle,
  } = setting;

  const { isBlocked, blockedContent, pageHead, breadcrumbs } = useSpecialPage({
    specialPageTitle,
    gaLabelKey: specialPageShortName,
    specialPagePath,
    isAuthenticated,
    isLicenseVerified,
    isCorporationAccount,
    isPublisherAccount,
    isDoctorRecruiterAccount,
    isDemoAccount,
    mainImagePath,
    isPrivate,
  });

  if (isBlocked) {
    return blockedContent;
  }

  return (
    <>
      {pageHead}
      <Box pt={[14, 14, 14, 16]} bg="white">
        {breadcrumbs}
        <Grid
          templateRows={{ base: 'repeat(2, auto)', md: 'repeat(2, auto)' }}
          templateColumns={{ base: 'repeat(1, 1fr)', md: 'repeat(12, 1fr)' }}
          gap={8}
          pr={[0, 0, 4]}
          pl={[0, 0, 4]}
        >
          {/* main start */}
          <GridItem
            colSpan={{ base: 1, md: 8 }}
            overflow={{ base: 'hidden', md: 'visible' }}
          >
            <Box pr={[4, 6, 0]} pl={[4, 6, 0]} mb={4}>
              <Box pos="relative" mb={2}>
                <NextImage
                  src={mainImagePath}
                  alt={specialPageTitle}
                  width={1300}
                  height={720}
                />
              </Box>
              <Box
                p={{ base: 4, md: 6 }}
                borderRadius={5}
                borderColor="gray"
                borderWidth={1}
              >
                <Box mb={4}>{description}</Box>
              </Box>
            </Box>
            {/* main end */}
            {/* 3rd start */}
            <Box pr={[4, 6, 0]} pl={[4, 6, 0]}>
              {articles.length > 0 && (
                <Box pt={2} pb={2}>
                  <Flex justifyContent="space-between" mb={1}>
                    <Text fontWeight="bold" fontSize="md">
                      {listTitle || `${tagsString(tags)} に関するスライド`}
                    </Text>
                  </Flex>
                  <Flex direction={'column'}>
                    <ArticlesGrid
                      articles={articles}
                      isMobile={isMobile}
                      gaEventLabel={`${specialPageShortName}_tagsearch`}
                      responsibleColumns={specialPageRegularColumns}
                      onShowThumbnail={(articleId) => {
                        sendAnalyticsEvent(
                          'slide_thumbnail',
                          'show',
                          `special_${specialPageShortName}#${articleId}`,
                        );
                      }}
                      onClick={(articleId) => {
                        sendAnalyticsEvent(
                          'slide_thumbnail',
                          'click',
                          `special_${specialPageShortName}#${articleId}`,
                        );
                      }}
                    />
                  </Flex>
                </Box>
              )}
            </Box>
          </GridItem>
          {/* 3rd end */}
          {/* side start */}
          <GridItem colSpan={{ base: 1, md: 4 }} colStart={{ base: 1, md: 9 }}>
            <VStack spacing={4} pr={[4, 6, 0]} pl={[4, 6, 0]}>
              {sideBanner()}
            </VStack>
          </GridItem>
          {/* side end */}
        </Grid>
      </Box>
    </>
  );
};

export default ICtdIldPage;

export const getServerSideProps: GetServerSideProps<PageContext> = withAuth(
  async (context) => {
    const userId = context.req.user?.id;
    const userAgent = context.req?.headers['user-agent'] ?? '';

    createUserAccessHistory({
      userId,
      userAgent,
      gaCid: getGaCid(context.req),
      operation: userAccessHistoryOperation,
    });

    const { isPrivate, canDisplay } = await getSpecialArticleProps(
      specialPageSlug,
      userId,
    );
    if (!canDisplay) {
      return {
        notFound: true,
      };
    }

    const { tagsFromTagTexts, tagsFromAndTagTexts } =
      await getTagsWithTagSettings({
        tagTexts: setting.tagTexts,
        andTagTexts: setting.andTagTexts,
      });

    const [articleIdsFromTagTexts, articleIdsFromAndTagIds] = await Promise.all(
      [
        getArticleIdsWithTagIds(tagsFromTagTexts.map((tag) => tag.id)),
        getArticleIdsWithAndTagIds(
          tagsFromAndTagTexts.map((tags) => tags.map((tag) => tag.id)),
        ),
      ],
    );
    // 結果を結合し重複を削除
    const articleIdList: string[] = Array.from(
      new Set([...articleIdsFromTagTexts, ...articleIdsFromAndTagIds]),
    );

    const articleHeaders = await prisma.article_header.findMany({
      select: {
        article_id: true,
        name: true,
        article_type: true,
        status: true,
        top_img_m: true,
        session_count: true,
        like_count: true,
        disclose_scope: true,
        create_date: true,
        update_date: true,
        user_article_header_author_idTouser: true,
        article_tag_connection: {
          select: {
            tag: true,
          },
        },
        article_page: {
          select: {
            like_count: true,
          },
        },
        article_comments: {
          select: {
            id: true,
          },
        },
      },
      where: {
        article_id: {
          in: articleIdList,
        },
        status: 'DONE',
        hidden: false,
        is_deleted: false,
      },
      orderBy: {
        create_date: 'desc',
      },
    });
    // 投稿者がアカウント統合済みであればID基盤側のプロフィール情報を表示させる
    const discloseIds = articleHeaders
      .map((v) => v.user_article_header_author_idTouser.disclose_id ?? '')
      .filter((v) => !!v);
    const idBaseProfileMap = await fetchAccountIntegrationProfiles(discloseIds);

    const articles: ArticleHeader[] = articleHeaders.reduce(
      (articles: any, article: any) => {
        const author = article.user_article_header_author_idTouser;
        const idBaseProfile = idBaseProfileMap[author.disclose_id ?? ''];

        if (
          shouldNotShow(
            idBaseProfile,
            context.req.user,
            context.req.isAuthenticated(),
            article,
          )
        ) {
          return articles;
        }

        articles.push({
          articleId: article.article_id,
          name: article.name,
          articleType: article.article_type,
          status: article.status as ArticleStatus | null,
          topImageM: article.top_img_m,
          sessionCount: article.session_count || 0,
          likeCount:
            article.like_count +
            article.article_page.reduce(
              (prev: any, curr: any) => prev + curr.like_count,
              0,
            ),
          commentCount: article.article_comments.length,
          discloseScope: article.disclose_scope as DiscloseScope | null,
          createDate: date.toIso8601Jst(article.create_date),
          updateDate: date.toIso8601Jst(article.update_date),
          author: {
            userId: author.user_id,
            userName: idBaseProfile?.userName ?? null,
            // userNameが設定されている場合はfirstName, lastNameはフロントに返さないようにする
            lastName: idBaseProfile?.userName
              ? null
              : idBaseProfile?.lastName ?? author.last_name,
            firstName: idBaseProfile?.userName
              ? null
              : idBaseProfile?.firstName ?? author.first_name,
            updateDateEpochMilliseconds: author.update_date.getTime(),
            profilePhotoS3Key: author.profile_photo_s3_key,
            idBaseProfileUrl: idBaseProfile?.profilePhotoUrl ?? null,
            antaaAccountType: idBaseProfile?.accountType ?? null,
            antaaAccountSubType: idBaseProfile?.accountSubType ?? null,
            isOfficialAccount: idBaseProfile?.isOfficialAccount ?? null,
          },
          tags: article.article_tag_connection.map((conn: any) => ({
            id: conn.tag.tag_id,
            name: conn.tag.tag_name,
          })),
        });
        return articles;
      },
      [],
    );

    const isMobile = isMobileDevice(context.req.headers['user-agent']).any;
    const isDemoAccount = hasDemoAuthority(context.req.user?.userRoles);

    return {
      props: {
        tags: tagsFromTagTexts,
        articles,
        isMobile,
        isDemoAccount,
        isAuthenticated: context.req.isAuthenticated(),
        isLicenseVerified: isLicenseVerified(context.req.user ?? null),
        isCorporationAccount:
          context.req.user?.antaaAccountType === AntaaAccountType.CORPORATION,
        isPublisherAccount: isPublisherAccount(context.req.user),
        isDoctorRecruiterAccount: isDoctorRecruiterAccount(context.req.user),
        isPrivate,
      },
    };
  },
);
